/**
 * Product Feature Section JavaScript
 * Handles: Swiper with Thumbs, Read more expand/collapse, Quantity selector, Add to cart
 */

class ProductFeature {
  constructor(section) {
    this.section = section;
    this.descriptionWrapper = section.querySelector('[data-expandable]');
    this.readMoreBtn = section.querySelector('[data-toggle-expand]');
    this.quantityWrapper = section.querySelector('.product-feature__quantity');
    this.addToCartBtn = section.querySelector('.product-feature__atc-button');

    // Swiper elements
    this.mainSwiperEl = section.querySelector('.product-feature__main-swiper');
    this.prevBtn = section.querySelector('.product-feature__nav-arrow--prev');
    this.nextBtn = section.querySelector('.product-feature__nav-arrow--next');
    this.paginationEl = section.querySelector('.product-feature__pagination');

    this.quantity = 1;
    this.isExpanded = false;
    this.mainSwiper = null;

    this.init();
  }

  init() {
    this.initSwiper();
    this.initReadMore();
    this.initQuantitySelector();
    this.initAddToCart();
  }

  // Main Swiper initialization
  initSwiper() {
    if (!this.mainSwiperEl || typeof Swiper === 'undefined') return;

    // Initialize Main Swiper
    this.mainSwiper = new Swiper(this.mainSwiperEl, {
      spaceBetween: 0,
      loop: false,
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
      navigation: {
        nextEl: this.nextBtn,
        prevEl: this.prevBtn,
      },
      pagination: {
        el: this.paginationEl,
        clickable: true,
      },
    });
  }

  // Read More / Collapse functionality
  initReadMore() {
    if (!this.readMoreBtn || !this.descriptionWrapper) return;

    this.readMoreBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleReadMore();
    });
  }

  toggleReadMore() {
    this.isExpanded = !this.isExpanded;
    this.descriptionWrapper.classList.toggle('is-expanded', this.isExpanded);

    const iconEl = this.readMoreBtn.querySelector('[data-icon]');
    const textEl = this.readMoreBtn.querySelector('[data-text]');

    if (iconEl) {
      iconEl.textContent = this.isExpanded ? '-' : '+';
    }
    if (textEl) {
      const strings = window.theme?.strings || {};
      textEl.textContent = this.isExpanded ? (strings.readLess || 'Lire moins') : (strings.readMore || 'Lire plus');
    }
  }

  // Quantity Selector dropdown
  initQuantitySelector() {
    if (!this.quantityWrapper) return;

    this.quantitySelect = this.quantityWrapper.querySelector('.product-feature__quantity-select');
    this.quantityDisplay = this.quantityWrapper.querySelector('.product-feature__quantity-display');

    if (!this.quantitySelect) return;

    this.quantitySelect.addEventListener('change', () => {
      this.quantity = parseInt(this.quantitySelect.value, 10);
      if (this.quantityDisplay) {
        this.quantityDisplay.textContent = this.quantity;
      }
    });
  }

  // Add to Cart functionality
  initAddToCart() {
    if (!this.addToCartBtn) return;

    this.addToCartBtn.addEventListener('click', async (e) => {
      e.preventDefault();

      const variantId = this.addToCartBtn.dataset.variantId;
      if (!variantId) {
        console.warn('No variant ID found');
        return;
      }

      const originalText = this.addToCartBtn.textContent;
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.textContent = window.theme?.strings?.adding || 'Ajout en cours...';

      try {
        await this.addToCart(variantId, this.quantity);

        this.addToCartBtn.textContent = window.theme?.strings?.added || 'Ajouté !';

        // Dispatch events for cart drawer
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('cart:open'));

        setTimeout(() => {
          this.addToCartBtn.disabled = false;
          this.addToCartBtn.textContent = originalText;
        }, 2000);
      } catch (error) {
        console.error('Add to cart error:', error);
        this.addToCartBtn.textContent = window.theme?.strings?.error || 'Erreur';

        setTimeout(() => {
          this.addToCartBtn.disabled = false;
          this.addToCartBtn.textContent = originalText;
        }, 2000);
      }
    });
  }

  async addToCart(variantId, quantity) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: parseInt(variantId),
        quantity: quantity
      })
    });

    if (!response.ok) {
      throw new Error('Failed to add to cart');
    }

    return await response.json();
  }

  // Destroy swiper on section unload
  destroy() {
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }
  }
}

// Store instances for cleanup
const productFeatureInstances = new Map();

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const productFeatureSections = document.querySelectorAll('.product-feature');

  productFeatureSections.forEach(section => {
    const instance = new ProductFeature(section);
    productFeatureInstances.set(section.dataset.sectionId, instance);
  });
});

// Re-initialize on Shopify section load (for theme editor)
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.classList.contains('product-feature')) {
    const instance = new ProductFeature(event.target);
    productFeatureInstances.set(event.target.dataset.sectionId, instance);
  }
});

// Cleanup on Shopify section unload (for theme editor)
document.addEventListener('shopify:section:unload', (event) => {
  if (event.target.classList.contains('product-feature')) {
    const instance = productFeatureInstances.get(event.target.dataset.sectionId);
    if (instance) {
      instance.destroy();
      productFeatureInstances.delete(event.target.dataset.sectionId);
    }
  }
});
